import React, {useEffect, useMemo, useRef, useState} from "react";
import {getDocumentCaptureOrigin, parseDocumentCaptureMessage} from "../../config/documentCapture";
import {
    DOCUMENT_CAPTURE_PHASE_CREATING,
    DOCUMENT_CAPTURE_PHASE_DELIVERED,
    DOCUMENT_CAPTURE_PHASE_ERROR,
    DOCUMENT_CAPTURE_PHASE_IDLE,
    DOCUMENT_CAPTURE_PHASE_READY,
    DOCUMENT_CAPTURE_PHASE_WAITING,
} from "./useDocumentCaptureSession";

const STEP_LABELS = {
    drivers_license: "Rijbewijs",
    passport: "Paspoort",
    id_card: "Identiteitskaart",
    selfie: "Selfie",
};

const getStepLabel = (step) => {
    const documentType = String(step?.document_type || "").toLowerCase();
    const base = STEP_LABELS[documentType] || documentType || "Document";
    const side = String(step?.side || "").toLowerCase();
    if (!side || documentType === "selfie") return base;
    return `${base} (${side === "front" ? "voorkant" : side === "back" ? "achterkant" : side})`;
};

/**
 * Renders one Document Capture session for the customer:
 * - desktop: QR-code + manual link, the photos are taken on the phone;
 * - mobile: the capture URL embedded in an iframe (plus a new-tab fallback).
 *
 * Lifecycle messages from the iframe only speed up the status polling; the
 * session status from the API stays the source of truth.
 */
const DocumentCapturePanel = ({
    isMobileFlow,
    phase,
    error,
    pollWarning,
    session,
    sessionStatus,
    onRetry,
    onCaptureMessage,
    onBack,
    onFrameLoad,
}) => {
    const iframeRef = useRef(null);
    const [isIframeReady, setIsIframeReady] = useState(false);
    const captureUrl = session?.url || "";
    const captureOrigin = useMemo(() => getDocumentCaptureOrigin(captureUrl), [captureUrl]);
    const qrCode = session?.qr_code || "";
    const steps = Array.isArray(sessionStatus?.steps) ? sessionStatus.steps : [];
    const isWaiting = phase === DOCUMENT_CAPTURE_PHASE_WAITING;
    const isCreating = phase === DOCUMENT_CAPTURE_PHASE_IDLE || phase === DOCUMENT_CAPTURE_PHASE_CREATING;
    const isFinishing = phase === DOCUMENT_CAPTURE_PHASE_READY || phase === DOCUMENT_CAPTURE_PHASE_DELIVERED;
    const isError = phase === DOCUMENT_CAPTURE_PHASE_ERROR;
    const allPhotosCaptured = Boolean(sessionStatus?.complete) || (steps.length > 0 && steps.every((step) => step?.captured));
    const shouldEmbedIframe = isMobileFlow && isWaiting && Boolean(captureUrl);

    useEffect(() => {
        setIsIframeReady(false);
    }, [captureUrl]);

    useEffect(() => {
        if (!shouldEmbedIframe) return undefined;

        const onMessage = (event) => {
            const eventName = parseDocumentCaptureMessage(event, {
                expectedOrigin: captureOrigin,
                expectedSource: iframeRef.current?.contentWindow || null,
            });
            if (!eventName) return;
            if (eventName === "ready") {
                setIsIframeReady(true);
            }
            if (typeof onCaptureMessage === "function") {
                onCaptureMessage(eventName);
            }
        };

        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [captureOrigin, onCaptureMessage, shouldEmbedIframe]);

    const renderStatusList = () => {
        if (!steps.length) return null;

        return (
            <div className="kav-register-mobile__status">
                {steps.map((step) => (
                    <span
                        key={step?.key || `${step?.document_type}-${step?.side}`}
                        className={`kav-register-mobile__statusItem ${step?.captured ? "is-success" : "is-pending"}`}
                    >
                        <i className={`bi ${step?.captured ? "bi-check-circle-fill" : "bi-camera-fill"}`}></i>
                        {getStepLabel(step)}
                    </span>
                ))}
            </div>
        );
    };

    const renderProgressCallout = () => {
        if (isCreating) {
            return (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Documentcontrole wordt voorbereid...</span>
                </div>
            );
        }

        if (isFinishing) {
            return (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Foto&apos;s zijn ontvangen. Gegevens worden verwerkt...</span>
                </div>
            );
        }

        if (isWaiting && allPhotosCaptured) {
            return (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Alle foto&apos;s zijn gemaakt. Je document wordt gecontroleerd...</span>
                </div>
            );
        }

        if (isWaiting && !isMobileFlow) {
            return (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>We wachten tot je de foto&apos;s op je telefoon hebt gemaakt.</span>
                </div>
            );
        }

        if (isWaiting && isMobileFlow && !isIframeReady) {
            return (
                <div className="kav-callout kav-auth-status">
                    <span className="kav-spinner" aria-hidden="true"></span>
                    <span>Documentcontrole wordt geladen...</span>
                </div>
            );
        }

        return null;
    };

    const renderDesktopQr = () => (
        <div className="kav-register-mobile__desktopQr">
            {qrCode ? (
                <img
                    className="kav-register-mobile__qrImage"
                    src={qrCode}
                    alt="QR-code voor documentcontrole op je telefoon"
                />
            ) : null}
            <p className="kav-register-mobile__help">
                Scan de QR-code met je telefoon en maak daar de foto&apos;s van je rijbewijs en een selfie.
                Deze pagina gaat automatisch verder zodra dat klaar is.
            </p>
        </div>
    );

    const renderMobileIframe = () => (
        <>
            <div className="kav-document-capture-frame">
                <iframe
                    ref={iframeRef}
                    key={captureUrl}
                    src={captureUrl}
                    allow="camera"
                    referrerPolicy="no-referrer"
                    title="Documentcontrole"
                    onLoad={onFrameLoad}
                />
            </div>
            <p className="kav-register-mobile__help">
                Werkt de camera niet in dit venster?{" "}
                <a
                    className="kav-register-mobile__qrLink"
                    href={captureUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Open de documentcontrole in een nieuw tabblad
                </a>
                {" "}en kom daarna terug naar deze pagina.
            </p>
        </>
    );

    return (
        <div className="kav-panel kav-register-mobile">
            <h2 className="kav-h2">Documentverificatie</h2>
            <p className="kav-register-mobile__text">
                {isMobileFlow
                    ? "Maak een foto van je rijbewijs en een selfie. Je gegevens worden automatisch uitgelezen."
                    : "Maak de foto's met je telefoon. Daarna ga je op deze computer verder met je registratie."}
            </p>

            {renderStatusList()}
            {renderProgressCallout()}

            {pollWarning && !isError && (
                <div className="kav-callout kav-auth-error">
                    {pollWarning}
                </div>
            )}

            {isError && (
                <div className="kav-callout kav-auth-error">
                    {error}
                </div>
            )}

            {isWaiting && !isMobileFlow && renderDesktopQr()}
            {shouldEmbedIframe && renderMobileIframe()}

            <div className="kav-auth-actions kav-register-mobile__actions kav-auth-actions--mobile-reverse">
                {isError && (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--primary"
                        onClick={onRetry}
                    >
                        Opnieuw proberen
                    </button>
                )}
                {isWaiting && !isMobileFlow && !allPhotosCaptured && (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                        onClick={onRetry}
                    >
                        Nieuwe controle starten
                    </button>
                )}
                {typeof onBack === "function" && !isFinishing && (
                    <button
                        type="button"
                        className="kav-step-actions__btn kav-step-actions__btn--ghost"
                        onClick={onBack}
                    >
                        Terug
                    </button>
                )}
            </div>
        </div>
    );
};

export default DocumentCapturePanel;
